/**
 * State alphabet and deadline constants for the call reconnection state machine.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/CallReconnectionState.swift`.
 *
 * The state machine enforces a strict progression:
 *
 *   connecting → connected → unstable → reconnecting → disconnectedAfterFailure
 *                                                    ↘ (or clean)
 *                                                      ended
 *
 * Both `disconnectedAfterFailure` and `ended` are terminal — no event or tick
 * can leave them.
 *
 * ## Key invariant
 * `disconnectedAfterFailure` is ONLY reachable from `tick(state, now)` after
 * the reconnecting deadline elapses. A single event (even a forged one) cannot
 * drive the machine into the terminal failure state; this prevents a passive
 * observer from spoofing call-end UI via a brief network drop.
 */

// ---------------------------------------------------------------------------
// State alphabet
// ---------------------------------------------------------------------------

/** The initial transient state before the first media connection. */
export type ConnectingState = {
  readonly kind: "connecting";
  /** Milliseconds since epoch when this state was entered. */
  readonly enteredAt: number;
};

/** Media connection is established and healthy. */
export type ConnectedState = {
  readonly kind: "connected";
};

/**
 * The connection is degraded but still nominally alive. The machine waits up
 * to `UNSTABLE_TO_RECONNECTING_MS` before escalating to `reconnecting`.
 */
export type UnstableState = {
  readonly kind: "unstable";
  /** Milliseconds since epoch when this state was entered. */
  readonly enteredAt: number;
  /**
   * Network path hint supplied by the consumer. Drives the UI banner copy.
   * `"cellular"` matches `navigator.connection.type === "cellular"` when
   * available; `"wifi"` covers ethernet/wifi; `"unknown"` is the safe default.
   *
   * Pure-logic module: the *consumer* reads `navigator.connection` and passes
   * the result here — this module never touches the DOM or browser APIs.
   */
  readonly networkPath: NetworkPath;
};

/**
 * ICE restart is in flight. If `connected` is not reached within
 * `RECONNECTING_TO_FAILED_MS` the machine transitions to
 * `disconnectedAfterFailure` — but ONLY via `tick(state, now)`.
 */
export type ReconnectingState = {
  readonly kind: "reconnecting";
  /** Milliseconds since epoch when reconnecting began. */
  readonly enteredAt: number;
};

/**
 * Terminal failure state. The reconnect deadline elapsed without recovery.
 * Only reachable via `tick(state, now)`.
 */
export type DisconnectedAfterFailureState = {
  readonly kind: "disconnectedAfterFailure";
};

/** Terminal clean state. The call was ended normally (hangup, remote hangup). */
export type EndedState = {
  readonly kind: "ended";
};

/** Union of all states. */
export type CallReconnectionState =
  | ConnectingState
  | ConnectedState
  | UnstableState
  | ReconnectingState
  | DisconnectedAfterFailureState
  | EndedState;

// ---------------------------------------------------------------------------
// Network path (consumer-supplied hint)
// ---------------------------------------------------------------------------

/**
 * Network path type hint. The consumer reads `navigator.connection.type` (if
 * exposed) and passes one of these values. This module stays DOM-free.
 */
export type NetworkPath = "cellular" | "wifi" | "unknown";

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/** The call media channel has successfully connected or reconnected. */
export type ConnectedEvent = { readonly kind: "connected" };

/**
 * The connection has degraded or dropped. Caller supplies the current network
 * path so the UI policy can show path-aware copy.
 */
export type NetworkDropEvent = {
  readonly kind: "networkDrop";
  readonly networkPath: NetworkPath;
};

/** The local or remote participant explicitly ended the call. */
export type CallEndedEvent = { readonly kind: "callEnded" };

/** Union of all events handled by `transition`. */
export type CallReconnectionEvent =
  | ConnectedEvent
  | NetworkDropEvent
  | CallEndedEvent;

// ---------------------------------------------------------------------------
// Deadline constants (milliseconds) — mirrors iOS
// ---------------------------------------------------------------------------

/**
 * Grace period in `unstable` before escalating to `reconnecting`.
 * iOS constant: `unstableToReconnectingDeadlineSeconds = 3`.
 */
export const UNSTABLE_TO_RECONNECTING_MS = 3_000;

/**
 * Maximum time in `reconnecting` before transitioning to
 * `disconnectedAfterFailure` (only via `tick`).
 * iOS constant: `reconnectingToFailedDeadlineSeconds = 30`.
 */
export const RECONNECTING_TO_FAILED_MS = 30_000;

// ---------------------------------------------------------------------------
// Constructor helpers
// ---------------------------------------------------------------------------

export function makeConnecting(now: number): ConnectingState {
  return { kind: "connecting", enteredAt: now };
}

export function makeConnected(): ConnectedState {
  return { kind: "connected" };
}

export function makeUnstable(now: number, networkPath: NetworkPath): UnstableState {
  return { kind: "unstable", enteredAt: now, networkPath };
}

export function makeReconnecting(now: number): ReconnectingState {
  return { kind: "reconnecting", enteredAt: now };
}

export const DISCONNECTED_AFTER_FAILURE: DisconnectedAfterFailureState = {
  kind: "disconnectedAfterFailure",
};

export const ENDED: EndedState = { kind: "ended" };
